import { useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { FileText, CheckCircle, Tag, Brain } from "lucide-react";
import { type TranscriptionData, useUpdateSummaryEditorStateMutation } from "@/redux/api/transcriptions.rtkq";
import { toast } from "sonner";
import TipTapEditor from "@/components/ui/shadcn-io/editor/TipTapEditor";

import type { HTMLContent } from "@tiptap/core";

interface SummaryViewerProps {
  summary: NonNullable<TranscriptionData["summary"]>;
  transcriptionId: string;
  userId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const SummaryViewer = ({ summary, transcriptionId, userId, isOpen, onOpenChange }: SummaryViewerProps) => {
  // Declare mutation
  const [updateSummaryEditorState] = useUpdateSummaryEditorStateMutation();

  // Debounced editor state update with auto-save
  const debouncedSetEditorState = useCallback(
    async (value: HTMLContent) => {
      try {
        await updateSummaryEditorState({
          userId: userId,
          transcriptionId: transcriptionId,
          htmlContent: value,
        }).unwrap();
      } catch (error) {
        // console.error("Error saving summary editor state:", error); // there is clg when rethrow back to TipTapEditor
        toast.error("שגיאה בשמירת השינויים");
        throw error; // Re-throw the error so TipTapEditor knows save failed
      }
    },
    [userId, transcriptionId, updateSummaryEditorState]
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] p-0 flex flex-col">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle className="max-w-4xl text-xl font-semibold text-chen-900 text-right break-all">
              {summary.title || `סיכום ${transcriptionId}`}
            </DialogTitle>
          </div>

          <DialogDescription className="text-muted-foreground text-right">צפייה בסיכום של תמלול קובץ אודיו</DialogDescription>

          {/* Metadata */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Badge variant="secondary" className="bg-purple-50 text-purple-700">
              <Brain className="w-3 h-3 ml-1" />
              סיכום
            </Badge>
            <Badge variant="secondary" className="bg-blue-50 text-blue-700">
              <FileText className="w-3 h-3 ml-1" />
              תמלול {transcriptionId}
            </Badge>
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-green-500" />
              הושלם
            </div>
          </div>

          {/* Tags */}
          {summary.tags && summary.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {summary.tags.map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  <Tag className="w-2 h-2 ml-1" />
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </DialogHeader>
        <Separator />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Summary Content */}
          <div className="flex-1 overflow-hidden pb-4 px-6">
            {/* TODO: // remove the or summary.text in the future after migration */}
            <TipTapEditor fileName={summary.title} summaryHTML={summary?.html?.edit as HTMLContent || summary?.text as HTMLContent} onUpdateCallback={debouncedSetEditorState} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SummaryViewer;
